import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMapPin, FiClock, FiStar, FiHeart, FiMessageCircle, FiGlobe, FiFacebook, FiYoutube, FiAlertTriangle } = FiIcons;

const Footer = ({ onBooking }) => {
  return (
    <footer className="py-16 px-4 bg-slate-900 relative overflow-hidden">
      {/* Enhanced Background */}
      <div className="absolute inset-0 opacity-15">
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395156128-20240204120138_IMG_1809.JPG" 
          alt="Background" 
          className="absolute top-0 left-0 w-1/2 h-full object-cover rounded-r-3xl" 
        />
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751394018933-20231003_224303.jpg" 
          alt="Background" 
          className="absolute bottom-0 right-0 w-1/3 h-2/3 object-cover rounded-tl-3xl opacity-80" 
        />
      </div>
      
      <div className="container mx-auto relative z-10">
        {/* Final CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-600">
                ชีวิตคุณควรค่าแก่การมีความมั่งคั่ง
              </span>
            </h2>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              อย่าปล่อยให้โอกาสทองนี้ผ่านไป<br />
              <span className="text-red-400 font-semibold">ใกล้ปิดรับสมัครแล้ว!</span>
            </p>
            
            {/* Critical Deadline Alert */}
            <div className="bg-gradient-to-r from-red-500/20 to-orange-600/20 rounded-xl p-8 border border-red-500/30 mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <SafeIcon icon={FiAlertTriangle} className="text-red-400 text-3xl" />
                <h3 className="text-2xl font-bold text-red-400">ข้อมูลสำคัญ</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-red-400 font-bold text-lg mb-2">⏰ ใกล้ปิดรับสมัครแล้ว</p>
                  <p className="text-white text-xl mb-2">12 กรกฎาคม 2568</p>
                  <p className="text-amber-400 font-semibold">เวลา 09.00-17.00 น.</p>
                  <p className="text-slate-300 text-sm mt-2">
                    นักพรตเตรียมยันต์และอ.รวิวิเคราะห์ดวง
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-purple-400 font-bold text-lg mb-2">🔮 ยันต์ "ฮู้" จีน</p>
                  <p className="text-white text-xl mb-2">20 คนแรกเท่านั้น</p>
                  <p className="text-amber-400 font-semibold">มากกว่านี้ทำไม่ทัน</p>
                  <p className="text-slate-300 text-sm mt-2">
                    ต้องใช้เวลาสร้างนาน เฉพาะราย
                  </p>
                </div>
              </div>
              <p className="text-lg text-white">
                💰 ราคาพิเศษ 5,500 บาท (10 คนแรก) | หลักสูตรรองรับสูงสุด 35 คน
              </p>
            </div>
            
            {/* Date highlight */}
            <div className="bg-slate-700/40 backdrop-blur-md rounded-xl p-6 border border-slate-600/30 mb-8">
              <p className="text-2xl font-bold text-amber-400 mb-2">
                📅 วันเรียน: 12 กรกฎาคม 2568
              </p>
              <p className="text-lg text-white">
                ⏰ เวลา: 09.00-17.00 น. | 📍 S Ratchada Leisure Hotel ห้อง Galleria ชั้น 2
              </p>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onBooking}
              className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-16 py-6 rounded-full text-2xl font-bold shadow-2xl hover:shadow-red-500/25 transition-all duration-300 mb-8 animate-pulse"
            >
              จองออนไลน์เลย!
            </motion.button>
            
            <div className="grid md:grid-cols-3 gap-6 text-center mb-8">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <SafeIcon icon={FiPhone} className="text-amber-400 text-2xl mx-auto mb-2" />
                <p className="text-white font-semibold">โทรสอบถาม</p>
                <p className="text-amber-400 text-lg font-bold">098-850-5900</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <SafeIcon icon={FiMapPin} className="text-amber-400 text-2xl mx-auto mb-2" />
                <p className="text-white font-semibold">สถานที่</p>
                <p className="text-slate-300">S Ratchada Leisure Hotel<br />ห้อง Galleria ชั้น 2</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <SafeIcon icon={FiClock} className="text-amber-400 text-2xl mx-auto mb-2" />
                <p className="text-white font-semibold">เวลา</p>
                <p className="text-slate-300">09.00 - 17.00 น.</p>
              </div>
            </div>
            
            {/* Contact Information */}
            <div className="bg-gradient-to-r from-blue-500/20 to-indigo-600/20 rounded-xl p-6 border border-blue-500/30 mb-8">
              <h3 className="text-xl font-bold text-blue-400 mb-4">ช่องทางติดต่อเพิ่มเติม</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <SafeIcon icon={FiMessageCircle} className="text-green-400 text-2xl mx-auto mb-2" />
                  <p className="text-white font-semibold">LINE ID</p>
                  <p className="text-green-400">@fengshuix</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <SafeIcon icon={FiGlobe} className="text-blue-400 text-2xl mx-auto mb-2" />
                  <p className="text-white font-semibold">Website</p>
                  <p className="text-blue-400 text-sm">www.fengshuix.com</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <SafeIcon icon={FiFacebook} className="text-blue-500 text-2xl mx-auto mb-2" />
                  <p className="text-white font-semibold">Facebook</p>
                  <p className="text-blue-500 text-sm">facebook.com/fengshuix</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <SafeIcon icon={FiYoutube} className="text-red-500 text-2xl mx-auto mb-2" />
                  <p className="text-white font-semibold">YouTube</p>
                  <p className="text-red-500 text-sm">fengshuix</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footer Info */}
        <div className="border-t border-slate-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiStar} className="text-white text-xl" />
              </div>
              <div>
                <h3 className="text-white font-bold">BAZI MASTERY</h3>
                <p className="text-slate-400 text-sm">วิชาปาจื้อเรียกทรัพย์</p>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-slate-400 mb-2">
                จัดโดยทีมผู้เชี่ยวชาญด้านปาจื้อและพิธีกรรมโบราณ
              </p>
              <div className="flex items-center justify-center gap-2">
                <p className="text-slate-300">สร้างด้วย</p>
                <SafeIcon icon={FiHeart} className="text-red-400" />
                <p className="text-slate-300">เพื่อคนไทย</p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-amber-400 font-semibold text-lg">098-850-5900</p>
              <p className="text-slate-400 text-sm">พร้อมให้คำปรึกษา</p>
            </div>
          </div>
          
          <div className="text-center mt-8 pt-8 border-t border-slate-700">
            <p className="text-slate-500 text-sm">
              © 2025 BAZI MASTERY. การใช้<span className="text-amber-400">วิชาปาจื้อเรียกทรัพย์</span>นี้เพื่อเสริมสร้างความมั่งคั่งอย่างถูกต้อง
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;